import { Card, Space, Tooltip, Typography, theme } from 'antd';
import React from 'react';

const { useToken } = theme;

import { Pill, type PillTone } from '@/components/common/Pill';

const { Title, Text } = Typography;

export interface TeamMember {
  initials: string;
  name: string;
  title?: string;
  subtitle: string;
  percent?: number;
  isActive?: number; // 1 = active, 0 = inactive
  lastWorkedDate?: string | null;
}

export interface TeamCardProps {
  members: TeamMember[];
}

export function TeamCard({ members }: Readonly<TeamCardProps>): React.ReactElement {
  const { token } = useToken();

  const getRoleTone = (title: string | undefined): PillTone => {
    if (!title) {
      return 'neutral';
    }
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('deal owner')) {
      return 'info';
    }
    if (lowerTitle.includes('delivery lead')) {
      return 'accent';
    }
    if (lowerTitle.includes('pm')) {
      return 'neutral';
    }
    return 'neutral';
  };

  return (
    <Card className="team-card">
      <div className="card__header">
        <Title level={5} className="team-card__title">
          Team
        </Title>
        <Pill tone="neutral">Source: Manual</Pill>
      </div>

      <Space direction="vertical" size="middle" className="team-card__members">
        {members.map((member) => {
          const roleTone = getRoleTone(member.title);
          const isInactive = member.isActive === 0;
          const tooltipTitle = isInactive
            ? member.lastWorkedDate
              ? `No longer active since ${member.lastWorkedDate}`
              : 'No longer active'
            : null;

          const memberElement = (
            <div
              key={member.name}
              className="team-card__member"
              style={isInactive ? { opacity: 0.6 } : undefined}
            >
              <div className="team-card__member-info">
                <div className="team-card__member-avatar">{member.initials}</div>
                <div className="team-card__member-details">
                  <div className="team-card__member-name">
                    {member.name}
                    {member.title && (
                      <Pill size="small" tone={roleTone} className="team-card__member-role">
                        {member.title}
                      </Pill>
                    )}
                  </div>
                  <Text type="secondary" className="team-card__member-subtitle">
                    {member.subtitle}
                  </Text>
                </div>
              </div>
              {member.percent !== undefined && (
                <Text
                  className="team-card__member-percent"
                  style={{ color: member.percent > 100 ? token.colorError : undefined }}
                >
                  {member.percent}%
                </Text>
              )}
            </div>
          );

          return isInactive && tooltipTitle ? (
            <Tooltip key={member.name} title={tooltipTitle}>
              {memberElement}
            </Tooltip>
          ) : (
            memberElement
          );
        })}
      </Space>
    </Card>
  );
}
